import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Camera, ImageIcon, Wand2, Loader2, Trash2, ChevronLeft,
  History as HistoryIcon, ArrowLeftRight, ShoppingBag, Palette, Shirt, Upload,
} from 'lucide-react';
import CelebMirror from './CelebMirror';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fileToCompressedBase64(file, maxEdge = 1400, quality = 0.85) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

function BeforeAfter({ before, after }) {
  const [pos, setPos] = useState(50);
  const wrap = useRef(null);
  const dragging = useRef(false);
  const onMove = (clientX) => {
    if (!wrap.current) return;
    const rect = wrap.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(pct);
  };
  useEffect(() => {
    const up = () => { dragging.current = false; };
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, []);
  return (
    <div
      ref={wrap}
      className="relative w-full aspect-[3/4] overflow-hidden border-[3px] border-black rounded-2xl bg-black cursor-ew-resize select-none"
      onMouseDown={(e) => { dragging.current = true; onMove(e.clientX); }}
      onMouseMove={(e) => dragging.current && onMove(e.clientX)}
      onTouchStart={(e) => { dragging.current = true; onMove(e.touches[0].clientX); }}
      onTouchMove={(e) => dragging.current && onMove(e.touches[0].clientX)}
      data-testid="makeup-before-after"
    >
      <img src={after} alt="efter" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt="före" className="absolute inset-0 h-full object-cover" style={{ width: `${100 / (pos / 100 || 0.0001)}%`, maxWidth: 'none' }} />
      </div>
      <div className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_0_2px_rgba(0,0,0,0.9)] pointer-events-none" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border-[3px] border-black rounded-full w-9 h-9 flex items-center justify-center shadow-[3px_3px_0_#000]">
          <ArrowLeftRight className="w-4 h-4" />
        </div>
      </div>
      <div className="absolute top-3 left-3 bg-white border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold pointer-events-none">FÖRE</div>
      <div className="absolute top-3 right-3 bg-[#FFC8DD] border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold pointer-events-none">EFTER ✨</div>
    </div>
  );
}

function StepCard({ step }) {
  return (
    <div className="neo-card p-3 bg-white flex gap-3 items-start" data-testid="makeup-step">
      <div className="w-9 h-9 rounded-xl border-[3px] border-black bg-[#FFC8DD] font-bold flex items-center justify-center shadow-[2px_2px_0_#000] shrink-0">
        {step.number}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-sm leading-tight mb-0.5">{step.title}</h4>
        <p className="text-xs text-black/80 leading-relaxed">{step.detail}</p>
        {step.tip && (
          <p className="text-[11px] text-black/60 mt-1 italic">💡 {step.tip}</p>
        )}
      </div>
    </div>
  );
}

function ColorSwatch({ c }) {
  return (
    <div className="flex items-center gap-2 bg-white border-2 border-black rounded-full px-2.5 py-1">
      <span
        className="w-4 h-4 rounded-full border-2 border-black"
        style={{ background: c.hex }}
      />
      <span className="text-xs font-bold">{c.name}</span>
      {c.use && <span className="text-[10px] text-black/55">· {c.use}</span>}
    </div>
  );
}

function OutfitSection({ entry, onOutfitCreated }) {
  const [open, setOpen] = useState(false);
  const [bodyPreview, setBodyPreview] = useState(null);
  const [bodyData, setBodyData] = useState(null);
  const [prefs, setPrefs] = useState('');
  const [generating, setGenerating] = useState(false);
  const bodyInputRef = useRef(null);
  const outfit = entry.outfit;

  const pickBody = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Välj en bildfil'); return; }
    try {
      const dataUrl = await fileToCompressedBase64(file);
      setBodyPreview(dataUrl);
      setBodyData(dataUrl);
    } catch {
      toast.error('Kunde inte läsa bilden');
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/makeup/entries/${entry.id}/outfit`,
        {
          full_body_image_base64: bodyData || null,
          preferences: prefs || null,
        },
        { withCredentials: true, timeout: 120000 }
      );
      toast.success('👗 Din klädarketyp är klar!');
      onOutfitCreated(data.outfit);
      setOpen(false);
      setBodyPreview(null);
      setBodyData(null);
      setPrefs('');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa outfit-förslag');
    } finally {
      setGenerating(false);
    }
  };

  if (outfit) {
    return (
      <div className="space-y-3" data-testid="outfit-result">
        <div className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#FFF7E0] to-[#E8F0FF]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-3xl leading-none">{outfit.archetype_emoji || '👗'}</span>
            <div>
              <p className="text-[10px] font-bold opacity-60 uppercase tracking-wide">Din klädarketyp</p>
              <h3 className="font-['Fredoka'] font-bold text-xl">{outfit.archetype}</h3>
            </div>
          </div>
          <p className="text-sm leading-relaxed mt-2">{outfit.archetype_description}</p>
          {outfit.body_observations && (
            <p className="text-xs leading-relaxed mt-2 italic text-black/75">
              💗 {outfit.body_observations}
            </p>
          )}
          {outfit.color_harmony && (
            <p className="text-xs leading-relaxed mt-2 text-black/75">
              🎨 {outfit.color_harmony}
            </p>
          )}
        </div>

        {outfit.outfits?.length > 0 && (
          <div>
            <h4 className="font-bold mb-2 flex items-center gap-2">
              <Shirt className="w-4 h-4" /> Outfit-förslag
            </h4>
            <div className="space-y-3">
              {outfit.outfits.map((o, i) => (
                <div key={i} className="neo-card p-3 bg-white" data-testid="outfit-card">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <h5 className="font-bold text-sm flex items-center gap-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-[2px] border-black bg-[#FFE8A3]">
                        {o.occasion}
                      </span>
                    </h5>
                    {o.colors?.length > 0 && (
                      <div className="flex gap-1">
                        {o.colors.slice(0, 4).map((c, j) => (
                          <span
                            key={j}
                            className="w-5 h-5 rounded-full border-2 border-black shadow-[1px_1px_0_#000]"
                            style={{ background: c }}
                            title={c}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <ul className="text-xs space-y-1.5 mb-2">
                    {(o.pieces || []).map((p, j) => (
                      <li key={j} className="flex gap-2">
                        <span className="opacity-50">•</span>
                        <div className="flex-1">
                          <span className="font-bold">{p.item}:</span>{' '}
                          <span className="text-black/80">{p.detail}</span>
                          {p.swedish_brand_hint && (
                            <span className="block text-[10px] text-black/55 mt-0.5">
                              🇸🇪 {p.swedish_brand_hint}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {o.why && (
                    <p className="text-xs italic text-black/70 border-t border-black/10 pt-2 mt-2">
                      ✨ {o.why}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {outfit.style_tips?.length > 0 && (
          <div className="neo-card p-3 bg-[#FDF0D5]">
            <h4 className="font-bold mb-1.5 text-sm">💡 Personliga stil-tips</h4>
            <ul className="text-xs space-y-1">
              {outfit.style_tips.map((t, i) => <li key={i}>• {t}</li>)}
            </ul>
          </div>
        )}

        {outfit.avoid?.length > 0 && (
          <div className="neo-card p-3 bg-[#FFF3F9]">
            <h4 className="font-bold mb-1.5 text-sm">🌸 Mindre smickrande — bara som guide</h4>
            <ul className="text-xs space-y-1 text-black/80">
              {outfit.avoid.map((t, i) => <li key={i}>• {t}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="neo-btn w-full py-3 bg-gradient-to-br from-[#FFE8F5] to-[#C8B6FF] font-bold flex items-center justify-center gap-2"
        data-testid="outfit-generate-btn"
      >
        <Shirt className="w-4 h-4" /> Få klädarketyp + outfit-förslag som matchar
      </button>
    );
  }

  return (
    <div className="neo-card p-4 bg-white space-y-3" data-testid="outfit-form">
      <div className="flex items-center justify-between">
        <h4 className="font-bold flex items-center gap-2"><Shirt className="w-4 h-4" /> Outfit-förslag</h4>
        <button onClick={() => setOpen(false)} className="neo-btn p-1 bg-white text-xs">✕</button>
      </div>
      <p className="text-xs text-black/70">
        Ladda gärna upp en helkroppsbild för mer träffsäkra förslag — annars räcker ansiktet du redan har.
      </p>
      {bodyPreview ? (
        <div className="relative">
          <img src={bodyPreview} alt="helkropp" className="w-full max-h-60 object-cover border-[3px] border-black rounded-xl" />
          <button
            onClick={() => { setBodyPreview(null); setBodyData(null); if (bodyInputRef.current) bodyInputRef.current.value = ''; }}
            className="absolute top-2 right-2 neo-btn p-1.5 bg-white"
            aria-label="Ta bort"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => bodyInputRef.current?.click()}
          className="w-full py-6 border-[3px] border-dashed border-black rounded-xl bg-[#FFF7E0] hover:bg-[#FFE8A3] font-bold text-sm flex items-center justify-center gap-2"
          data-testid="outfit-pick-body"
        >
          <Upload className="w-4 h-4" /> Ladda upp helkroppsbild (valfritt)
        </button>
      )}
      <input ref={bodyInputRef} type="file" accept="image/*" onChange={pickBody} className="hidden" />
      <input
        type="text"
        placeholder="Önskemål: t.ex. gillar byxor bäst, har inte mycket tid på morgonen"
        value={prefs}
        onChange={(e) => setPrefs(e.target.value)}
        className="neo-input w-full text-sm"
        maxLength={200}
        data-testid="outfit-prefs"
      />
      <button
        onClick={generate}
        disabled={generating}
        className="neo-btn w-full py-2 bg-gradient-to-br from-[#FFC8DD] to-[#C8B6FF] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="outfit-submit"
      >
        {generating ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Analyserar din stil... (ca 15-25 sek)</>
        ) : (
          <><Sparkles className="w-4 h-4" /> Skapa outfit-förslag</>
        )}
      </button>
    </div>
  );
}

function EntryDetail({ entry, onBack, onDelete, onEntryUpdated }) {
  const a = entry.analysis || {};
  return (
    <div className="space-y-4" data-testid="makeup-entry-detail">
      <button onClick={onBack} className="neo-btn px-3 py-1.5 bg-white text-sm flex items-center gap-1" data-testid="makeup-back">
        <ChevronLeft className="w-4 h-4" /> Tillbaka
      </button>

      {entry.preview_image ? (
        <BeforeAfter
          before={`data:image/jpeg;base64,${entry.original_image}`}
          after={`data:image/jpeg;base64,${entry.preview_image}`}
        />
      ) : (
        <div className="relative">
          <img
            src={`data:image/jpeg;base64,${entry.original_image}`}
            alt={entry.style_name}
            className="w-full aspect-[3/4] object-cover border-[3px] border-black rounded-2xl"
          />
          <div className="absolute top-3 left-3 bg-white border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold">ORIGINAL</div>
        </div>
      )}

      <div className="neo-card p-4 bg-[#FFF7E0]">
        <p className="text-xs font-bold opacity-70 flex items-center gap-1">
          {entry.style_emoji} {entry.style_name}
        </p>
        <p className="text-sm leading-relaxed mt-1" data-testid="makeup-observation">{a.observation}</p>
      </div>

      {a.color_palette?.length > 0 && (
        <div className="neo-card p-4 bg-white">
          <h3 className="font-bold flex items-center gap-2 mb-2"><Palette className="w-4 h-4" /> Din färgpalett</h3>
          <div className="flex flex-wrap gap-2" data-testid="makeup-palette">
            {a.color_palette.map((c, i) => <ColorSwatch key={i} c={c} />)}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Steg för steg ({a.steps?.length || 0})
        </h3>
        <div className="space-y-2">
          {(a.steps || []).map((s, i) => <StepCard key={i} step={s} />)}
        </div>
      </div>

      {a.product_suggestions?.length > 0 && (
        <div className="neo-card p-4 bg-white">
          <h3 className="font-bold flex items-center gap-2 mb-2"><ShoppingBag className="w-4 h-4" /> Produktförslag</h3>
          <div className="space-y-3">
            {a.product_suggestions.map((p, i) => (
              <div key={i} className="border-l-4 border-[#FFC8DD] pl-3">
                <p className="font-bold text-sm">{p.type}</p>
                <p className="text-xs text-black/70 leading-relaxed">{p.look_for}</p>
                {p.alternatives?.length > 0 && (
                  <ul className="text-xs mt-1 space-y-0.5">
                    {p.alternatives.map((alt, j) => (
                      <li key={j} className="flex justify-between gap-2">
                        <span><b>{alt.brand}</b> {alt.name}</span>
                        <span className="text-black/55 shrink-0">{alt.price_hint || ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {p.budget && (
                  <span className="text-[10px] font-bold mt-1 inline-block px-2 py-0.5 rounded-full border-2 border-black bg-[#FDF0D5]">
                    Budget: {p.budget}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {a.finish_tip && (
        <div className="neo-card p-3 bg-[#E8F5E8] text-sm text-center font-bold" data-testid="makeup-finish-tip">
          🌺 {a.finish_tip}
        </div>
      )}

      {/* OUTFIT integration */}
      <div className="pt-3 border-t-[3px] border-black/10">
        <OutfitSection
          entry={entry}
          onOutfitCreated={(outfit) => onEntryUpdated?.({ ...entry, outfit })}
        />
      </div>

      <button
        onClick={onDelete}
        className="neo-btn w-full py-2 bg-white text-red-600 flex items-center justify-center gap-2 text-sm"
        data-testid="makeup-delete"
      >
        <Trash2 className="w-4 h-4" /> Ta bort
      </button>
    </div>
  );
}

export default function MakeupStudio() {
  const [subTab, setSubTab] = useState('makeup');
  const [styles, setStyles] = useState([]);
  const [entries, setEntries] = useState([]);
  const [activeEntry, setActiveEntry] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('natural_day');
  const [compareMode, setCompareMode] = useState(false);
  const [compareStyleB, setCompareStyleB] = useState('soft_night');
  const [compareResult, setCompareResult] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [stylesRes, entriesRes] = await Promise.all([
        axios.get(`${API}/makeup/styles`, { withCredentials: true }),
        axios.get(`${API}/makeup/entries`, { withCredentials: true }),
      ]);
      setStyles(stylesRes.data.styles || []);
      setEntries(entriesRes.data.entries || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Välj en bildfil');
      return;
    }
    try {
      const dataUrl = await fileToCompressedBase64(file);
      setPreviewUrl(dataUrl);
      setFileData(dataUrl);
    } catch {
      toast.error('Kunde inte läsa bilden');
    }
  };

  const submit = async () => {
    if (!fileData) {
      toast.error('Välj en selfie först');
      return;
    }
    if (compareMode && selectedStyle === compareStyleB) {
      toast.error('Välj två olika stilar att jämföra');
      return;
    }
    setAnalyzing(true);
    try {
      if (compareMode) {
        const { data } = await axios.post(
          `${API}/makeup/compare`,
          {
            image_base64: fileData,
            style_a: selectedStyle,
            style_b: compareStyleB,
            notes: notes || null,
          },
          { withCredentials: true, timeout: 240000 }
        );
        // Fetch full data for both
        const [fullA, fullB] = await Promise.all([
          axios.get(`${API}/makeup/entries/${data.a.id}`, { withCredentials: true }),
          axios.get(`${API}/makeup/entries/${data.b.id}`, { withCredentials: true }),
        ]);
        setCompareResult({ a: fullA.data, b: fullB.data });
        toast.success('✨ Båda guiderna är klara!');
      } else {
        const { data } = await axios.post(
          `${API}/makeup/analyze`,
          {
            image_base64: fileData,
            style: selectedStyle,
            notes: notes || null,
            visualize: true,
          },
          { withCredentials: true, timeout: 180000 }
        );
        toast.success('✨ Din personliga guide är klar!');
        const { data: full } = await axios.get(`${API}/makeup/entries/${data.id}`, { withCredentials: true });
        setActiveEntry(full);
      }
      setPreviewUrl(null);
      setFileData(null);
      setNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte analysera bilden');
    } finally {
      setAnalyzing(false);
    }
  };

  const del = async () => {
    if (!activeEntry) return;
    if (!window.confirm('Ta bort denna sminkguide?')) return;
    try {
      await axios.delete(`${API}/makeup/entries/${activeEntry.id}`, { withCredentials: true });
      setActiveEntry(null);
      fetchAll();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const openEntry = async (id) => {
    try {
      const { data } = await axios.get(`${API}/makeup/entries/${id}`, { withCredentials: true });
      setActiveEntry(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Kunde inte öppna');
    }
  };

  if (activeEntry) {
    return (
      <EntryDetail
        entry={activeEntry}
        onBack={() => setActiveEntry(null)}
        onDelete={del}
        onEntryUpdated={(updated) => setActiveEntry(updated)}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="makeup-studio">
      {/* Sub-tabs: Smink / Kändis-spegel */}
      <div className="flex gap-2" data-testid="makeup-subtabs">
        <button
          onClick={() => setSubTab('makeup')}
          className={`neo-btn flex-1 py-2 text-sm font-bold flex items-center justify-center gap-1.5 ${
            subTab === 'makeup' ? 'bg-[#FFC8DD]' : 'bg-white'
          }`}
          data-testid="makeup-subtab-makeup"
        >
          💄 Smink-guide
        </button>
        <button
          onClick={() => setSubTab('celeb')}
          className={`neo-btn flex-1 py-2 text-sm font-bold flex items-center justify-center gap-1.5 ${
            subTab === 'celeb' ? 'bg-[#FFF7B5]' : 'bg-white'
          }`}
          data-testid="makeup-subtab-celeb"
        >
          🌟 Kändis-spegel
        </button>
      </div>

      {subTab === 'celeb' ? (
        <CelebMirror />
      ) : (
        <MakeupTab
          styles={styles}
          entries={entries}
          activeEntry={activeEntry}
          setActiveEntry={setActiveEntry}
          previewUrl={previewUrl}
          setPreviewUrl={setPreviewUrl}
          fileData={fileData}
          setFileData={setFileData}
          selectedStyle={selectedStyle}
          setSelectedStyle={setSelectedStyle}
          compareMode={compareMode}
          setCompareMode={setCompareMode}
          compareStyleB={compareStyleB}
          setCompareStyleB={setCompareStyleB}
          compareResult={compareResult}
          setCompareResult={setCompareResult}
          notes={notes}
          setNotes={setNotes}
          loading={loading}
          analyzing={analyzing}
          fileInputRef={fileInputRef}
          cameraInputRef={cameraInputRef}
          onPickFile={onPickFile}
          submit={submit}
          del={del}
          openEntry={openEntry}
          fetchAll={fetchAll}
        />
      )}
    </div>
  );
}

// Wrap original makeup UI in a sub-component
function MakeupTab(p) {
  const {
    styles, entries, activeEntry, setActiveEntry, previewUrl, setPreviewUrl, fileData, setFileData,
    selectedStyle, setSelectedStyle, compareMode, setCompareMode,
    compareStyleB, setCompareStyleB, compareResult, setCompareResult,
    notes, setNotes, loading, analyzing, fileInputRef, cameraInputRef,
    onPickFile, submit, del, openEntry, fetchAll,
  } = p;
  return (
    <div className="space-y-5" data-testid="makeup-studio-inner">
      <header className="neo-card p-5 bg-gradient-to-br from-[#FFE8F5] via-[#FFF7E0] to-[#E8F0FF] relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="font-['Fredoka'] font-bold text-xl flex items-center gap-2">
            <Wand2 className="w-5 h-5" /> Sminkspegel
          </h2>
          <p className="text-sm text-black/75 mt-1 max-w-xl leading-relaxed">
            Ladda upp en selfie, välj en stil, och få både en AI-förhandsvisning på DITT ansikte
            och en personlig steg-för-steg-guide anpassad efter dina drag. 💄✨
          </p>
          <p className="text-[11px] text-black/50 italic mt-1">🔒 Helt privat. Bara du ser bilderna.</p>
        </div>
        <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 leading-none">💄</div>
      </header>

      <div className="neo-card p-4 bg-white space-y-4" data-testid="makeup-compose">
        {/* Image upload */}
        {previewUrl ? (
          <div className="relative">
            <img src={previewUrl} alt="selfie" className="w-full max-h-80 object-cover border-[3px] border-black rounded-2xl" />
            <button
              onClick={() => { setPreviewUrl(null); setFileData(null); if (fileInputRef.current) fileInputRef.current.value = ''; if (cameraInputRef.current) cameraInputRef.current.value = ''; }}
              className="absolute top-2 right-2 neo-btn p-1.5 bg-white"
              aria-label="Ta bort"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="py-8 border-[3px] border-dashed border-black rounded-2xl bg-[#FFF7E0] hover:bg-[#FFE8A3] transition flex flex-col items-center gap-1 font-bold text-sm"
              data-testid="makeup-pick-image-btn"
            >
              <ImageIcon className="w-6 h-6" /> Från album
            </button>
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="py-8 border-[3px] border-dashed border-black rounded-2xl bg-[#FFE8F5] hover:bg-[#FFC8DD] transition flex flex-col items-center gap-1 font-bold text-sm"
              data-testid="makeup-camera-btn"
            >
              <Camera className="w-6 h-6" /> Ta selfie
            </button>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="makeup-file-input" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="user" onChange={onPickFile} className="hidden" data-testid="makeup-camera-input" />

        {/* Compare toggle */}
        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer" data-testid="makeup-compare-toggle">
          <input
            type="checkbox"
            checked={compareMode}
            onChange={(e) => { setCompareMode(e.target.checked); setCompareResult(null); }}
            className="w-4 h-4 accent-[#FFC8DD]"
          />
          <ArrowLeftRight className="w-4 h-4" /> Jämför två stilar sida vid sida
        </label>

        {/* Style picker(s) */}
        <div>
          <p className="text-xs font-bold mb-2">{compareMode ? 'Stil A' : 'Välj stil'}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="makeup-style-grid">
            {styles.map((s) => {
              const active = selectedStyle === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSelectedStyle(s.key)}
                  className={`neo-card p-3 text-left transition ${active ? 'translate-y-[-2px] bg-[#FFC8DD]' : 'bg-white'}`}
                  data-testid={`makeup-style-${s.key}`}
                >
                  <div className="text-2xl leading-none mb-1">{s.emoji}</div>
                  <p className="font-bold text-xs">{s.name}</p>
                  <p className="text-[10px] text-black/60 leading-tight mt-0.5">{s.description}</p>
                </button>
              );
            })}
          </div>
          {compareMode && (
            <>
              <p className="text-xs font-bold mb-2 mt-3">Stil B</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {styles.map((s) => {
                  const active = compareStyleB === s.key;
                  const disabled = s.key === selectedStyle;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => !disabled && setCompareStyleB(s.key)}
                      disabled={disabled}
                      className={`neo-card p-3 text-left transition ${active ? 'translate-y-[-2px] bg-[#C8B6FF]' : 'bg-white'} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
                      data-testid={`makeup-styleB-${s.key}`}
                    >
                      <div className="text-2xl leading-none mb-1">{s.emoji}</div>
                      <p className="font-bold text-xs">{s.name}</p>
                      <p className="text-[10px] text-black/60 leading-tight mt-0.5">{s.description}</p>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div>
          <label className="text-xs font-bold block mb-1">Önskemål (valfritt)</label>
          <input
            type="text"
            placeholder="T.ex. trött idag, vill framhäva ögonen, osäker på brynen"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="neo-input w-full"
            maxLength={180}
            data-testid="makeup-notes"
          />
        </div>

        <button
          onClick={submit}
          disabled={analyzing || !fileData}
          className="neo-btn w-full py-3 bg-gradient-to-br from-[#FFC8DD] to-[#C8B6FF] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="makeup-submit"
        >
          {analyzing ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Skapar {compareMode ? 'båda guiderna' : 'din guide'}... (ca {compareMode ? '30-60' : '15-30'} sek)</>
          ) : (
            <><Sparkles className="w-5 h-5" /> {compareMode ? 'Jämför båda stilarna' : 'Skapa min sminkguide'}</>
          )}
        </button>
      </div>

      {/* Compare result */}
      {compareResult && (
        <div className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] to-[#EFE8FF]" data-testid="makeup-compare-result">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4" /> Jämförelse
            </h3>
            <button
              onClick={() => setCompareResult(null)}
              className="neo-btn px-2 py-1 text-xs bg-white"
              data-testid="makeup-compare-close"
            >
              Stäng
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {['a', 'b'].map((k) => {
              const e = compareResult[k];
              if (!e) return null;
              return (
                <div key={k} className="neo-card p-3 bg-white" data-testid={`makeup-compare-${k}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-sm">{e.style_emoji} {e.style_name}</p>
                    <button
                      onClick={() => setActiveEntry(e)}
                      className="neo-btn px-2 py-1 text-[11px] bg-[#FFC8DD] font-bold"
                      data-testid={`makeup-compare-open-${k}`}
                    >
                      Öppna full guide →
                    </button>
                  </div>
                  {e.preview_image ? (
                    <img
                      src={`data:image/jpeg;base64,${e.preview_image}`}
                      alt={e.style_name}
                      className="w-full aspect-[3/4] object-cover border-[3px] border-black rounded-xl"
                    />
                  ) : (
                    <img
                      src={`data:image/jpeg;base64,${e.original_image}`}
                      alt="original"
                      className="w-full aspect-[3/4] object-cover border-[3px] border-black rounded-xl opacity-80"
                    />
                  )}
                  <p className="text-xs text-black/75 mt-2 leading-relaxed line-clamp-3 italic">
                    {e.analysis?.observation}
                  </p>
                  <p className="text-[10px] text-black/50 mt-1">{e.analysis?.steps?.length || 0} steg i full guide</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <HistoryIcon className="w-4 h-4" /> Dina sparade guider ({entries.length})
        </h3>
        {loading ? (
          <div className="text-center py-6 text-sm text-black/60">Laddar...</div>
        ) : entries.length === 0 ? (
          <div className="neo-card p-6 bg-white text-center text-sm text-black/60">
            Inga sminkguider sparade ännu. 🌸
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map((e) => (
              <button
                key={e.id}
                onClick={() => openEntry(e.id)}
                className="neo-card p-3 bg-white text-left hover:translate-y-[-1px] transition"
                data-testid="makeup-list-item"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-bold text-sm">{e.style_emoji} {e.style_name}</h4>
                  {e.has_preview && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border-[2px] border-black bg-[#C8B6FF] shrink-0">✨ Visualiserad</span>
                  )}
                </div>
                <p className="text-xs text-black/70 line-clamp-2">
                  {e.analysis?.observation || '—'}
                </p>
                <p className="text-[10px] text-black/50 mt-1">
                  {e.analysis?.steps?.length || 0} steg
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
